/**
 * CloudBillAuditHub.js  —  v34.97 (Feb 13 2026)
 *
 * Hub page at /cloud-bill-audit. Shows all 6 cloud audit options as
 * cards. Visitor picks a cloud → lands on the focused /:cloud-bill-audit
 * page. Single source of truth: imports CLOUD_CONFIGS from BillAudit.js.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Clock, Lock } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import PublicNavbar from '../components/PublicNavbar';
import { CLOUD_CONFIGS } from './BillAudit';

const ACCENT_CARD = {
  emerald: { ring: 'ring-emerald-500/30', bg: 'bg-emerald-500/15', text: 'text-emerald-300', glow: 'shadow-emerald-500/20', border: 'hover:border-emerald-500/40' },
  sky:     { ring: 'ring-sky-500/30',     bg: 'bg-sky-500/15',     text: 'text-sky-300',     glow: 'shadow-sky-500/20',     border: 'hover:border-sky-500/40' },
  amber:   { ring: 'ring-amber-500/30',   bg: 'bg-amber-500/15',   text: 'text-amber-300',   glow: 'shadow-amber-500/20',   border: 'hover:border-amber-500/40' },
  cyan:    { ring: 'ring-cyan-500/30',    bg: 'bg-cyan-500/15',    text: 'text-cyan-300',    glow: 'shadow-cyan-500/20',    border: 'hover:border-cyan-500/40' },
  rose:    { ring: 'ring-rose-500/30',    bg: 'bg-rose-500/15',    text: 'text-rose-300',    glow: 'shadow-rose-500/20',    border: 'hover:border-rose-500/40' },
  blue:    { ring: 'ring-blue-500/30',    bg: 'bg-blue-500/15',    text: 'text-blue-300',    glow: 'shadow-blue-500/20',     border: 'hover:border-blue-500/40' },
};

const ORDER = ['aws', 'azure', 'gcp', 'snowflake', 'databricks', 'digitalocean'];

export default function CloudBillAuditHub() {
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <SEOHead
        title="Audit any cloud bill in 60 seconds · CARTIEAI"
        description="Free CSV audit for AWS, Azure, Google Cloud, Snowflake, Databricks, and DigitalOcean. Drop your billing export → top 5 leaks ranked by $/month + 3 quick-win actions. No signup, no credentials."
        path="/cloud-bill-audit"
      />
      <PublicNavbar />

      <main className="max-w-6xl mx-auto px-4 pt-24 pb-20">
        {/* Hero */}
        <header className="mb-10 text-center" data-testid="hub-hero">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/25 mb-4">
            <ShieldCheck className="w-3 h-3 text-emerald-300" />
            <span className="text-[11px] text-emerald-200 font-medium tracking-wide">6 clouds supported · zero credentials · free forever</span>
          </div>
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-3">
            Pick a cloud. <span className="text-emerald-300">Get an audit in 60 seconds.</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-300 max-w-2xl mx-auto">
            Drop your billing CSV. We find the top 5 cost leaks ranked by $/month and three quick-win actions you can do this week. No signup. No IAM keys. Your file is processed in-memory and never stored.
          </p>
        </header>

        {/* 6 cloud cards */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="hub-cloud-grid">
          {ORDER.map((key) => {
            const cfg = CLOUD_CONFIGS[key];
            const a = ACCENT_CARD[cfg.accent];
            const Icon = cfg.icon;
            return (
              <Link
                key={key}
                to={`/${key}-bill-audit`}
                data-testid={`hub-card-${key}`}
                className={`group relative block rounded-2xl bg-white/[0.03] border border-white/10 ${a.border} p-6 transition-all duration-300 hover:bg-white/[0.05] hover:-translate-y-0.5`}
              >
                <div className={`inline-flex items-center justify-center w-11 h-11 rounded-xl ${a.bg} ring-1 ${a.ring} shadow-lg ${a.glow} mb-4`}>
                  <Icon className={`w-5 h-5 ${a.text}`} strokeWidth={2.2} />
                </div>
                <h3 className="text-base font-semibold text-white mb-1.5 leading-snug">
                  Audit your {cfg.label} bill
                </h3>
                <p className="text-sm text-slate-400 mb-4 leading-relaxed">
                  Drop a CSV. Get the top 5 leaks ranked by $/month, plus 3 quick wins.
                </p>
                <p className="text-[11px] text-slate-500 mb-5 leading-relaxed">
                  Covers: {cfg.sample.split(' · ').slice(0, 4).join(' · ')}…
                </p>
                <span className={`inline-flex items-center gap-1.5 text-sm font-semibold ${a.text} transition-colors`}>
                  Run the audit
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </Link>
            );
          })}
        </section>

        {/* Trust strip */}
        <section className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="hub-trust-strip">
          <Trust icon={Clock}       title="60-second audit"       body="Drop your CSV, see results before the page finishes scrolling." />
          <Trust icon={Lock}        title="In-memory processing"  body="Your billing file never touches our database. Zero retention." />
          <Trust icon={ShieldCheck} title="No credentials needed" body="No IAM keys, no service principals, no OAuth. Just a CSV." />
        </section>

        {/* Soft CTA */}
        <section className="text-center mt-14 pt-8 border-t border-white/5" data-testid="hub-cta">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
            Want the audit + the <span className="text-emerald-300">auto-fix</span>?
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto mb-6">
            CARTIE plugs into your cloud and sends pull requests for every leak we find — right where your engineers already work. The free audit is just the first 60 seconds of what we do.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold transition-colors"
            data-testid="hub-signup-cta"
          >
            Start free — connect in 60 sec
            <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
      </main>
    </div>
  );
}

const Trust = ({ icon: Icon, title, body }) => (
  <div className="p-4 rounded-xl border border-white/10 bg-white/[0.02]">
    <Icon className="w-4 h-4 text-emerald-300 mb-2" />
    <p className="text-sm font-semibold text-white mb-1">{title}</p>
    <p className="text-xs text-slate-400 leading-relaxed">{body}</p>
  </div>
);
